import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  SoapAttribute,
  SoapAttributeSelection,
  SoapCategory,
  SoapGroup,
  SoapNotesService,
} from '../../soap-notes.service';
import { take } from 'rxjs/operators';

type SoapAttributesDialogButtons = Record<string, { text?: string }>;

export interface SoapAttributesDialogPayload {
  title?: string;
  selected?: string[];
  soapCategory?: SoapCategory;
  buttons?: SoapAttributesDialogButtons;
}

type SoapAttributesDialogValue = SoapAttributesDialogPayload | SoapAttributeSelection[];

@Component({
  selector: 'fb-soap-attributes-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  template: `
    <div class="dialog">
      <div class="dialog__filter">
        <div class="dialog__filter-label">Filter:</div>
        <ng-select
          class="dialog__filter-select"
          [items]="groupOptions"
          bindLabel="name"
          bindValue="id"
          [(ngModel)]="selectedGroupId"
          (change)="applyFilter()"
          [clearable]="false"
          [searchable]="false"
        >
        </ng-select>
      </div>

      <div class="dialog__list">
        <div class="dialog__loader" *ngIf="isLoading">
          <div class="dialog__spinner"></div>
          <div class="dialog__loader-text">Loading attributes...</div>
        </div>
        <ng-container *ngIf="!isLoading">
          <div
            class="dialog__row"
            *ngFor="let attribute of filteredAttributes"
            (click)="toggleAttribute(attribute)"
          >
            <input
              type="checkbox"
              [checked]="isSelected(attribute)"
              (click)="$event.stopPropagation()"
              (change)="toggleAttribute(attribute)"
            />
            <div class="dialog__row-text">{{ attribute.name }}</div>
          </div>
          <div class="dialog__empty" *ngIf="filteredAttributes.length === 0">No attributes</div>
        </ng-container>
      </div>
    </div>
  `,
  styles: [
    `
      .dialog {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .dialog__filter {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #4b5563;
      }

      .dialog__filter-select {
        width: auto;
      }

      .dialog__list {
        overflow-y: auto;
        overflow-x: hidden;
        min-height: 320px;
        max-height: 320px;
        scrollbar-width: none;
      }

      .dialog__list::-webkit-scrollbar {
        width: 0;
        height: 0;
      }

      .dialog__row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        border-bottom: 1px solid #e5e7eb;
        cursor: pointer;
      }

      .dialog__row:last-child {
        border-bottom: none;
      }

      .dialog__row-text {
        font-size: 14px;
        color: #111827;
      }

      .dialog__empty {
        padding: 12px;
        font-size: 14px;
        color: #6b7280;
      }

      .dialog__loader {
        height: 320px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #6b7280;
        font-size: 13px;
      }

      .dialog__spinner {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid #e5e7eb;
        border-top-color: #2563eb;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .dialog__row input[type='checkbox'] {
        appearance: none;
        width: 16px;
        height: 16px;
        border: 1px solid #e5e7eb;
        border-radius: 3px;
        display: inline-grid;
        place-content: center;
        background: #fff;
      }

      .dialog__row input[type='checkbox']::after {
        content: '';
        width: 4px;
        height: 8px;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg) scale(0);
        transition: transform 120ms ease-in-out;
      }

      .dialog__row input[type='checkbox']:checked {
        border-color: #2563eb;
        background: #2563eb;
      }

      .dialog__row input[type='checkbox']:checked::after {
        transform: rotate(45deg) scale(1);
      }

      :host ::ng-deep .dialog__filter-select .ng-select-container {
        border: none;
        box-shadow: none;
        background: transparent;
        min-height: auto;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
      }

      :host ::ng-deep .dialog__filter-select .ng-input {
        display: none;
      }

      :host ::ng-deep .dialog__filter-select .ng-value-container {
        padding: 0;
      }

      :host ::ng-deep .dialog__filter-select .ng-value {
        font-size: 14px;
        color: #111827;
      }

      :host ::ng-deep .dialog__filter-select .ng-arrow-wrapper {
        padding: 0;
        margin-left: 4px;
      }

      :host ::ng-deep .dialog__filter-select .ng-arrow {
        border-color: #2563eb transparent transparent;
      }
    `,
  ],
})
export class SoapAttributesDialogComponent implements OnInit {
  payload: SoapAttributesDialogValue = {};
  groupOptions: Array<SoapGroup & { id: string }> = [];
  attributes: SoapAttribute[] = [];
  filteredAttributes: SoapAttribute[] = [];
  private categoryAttributes: SoapAttribute[] = [];
  selectedGroupId = 'all';
  private selected = new Set<string>();
  isLoading = true;
  private isLoadingGroups = true;
  private isLoadingAttributes = true;
  private categoryGroupMap = new Map<string, string>();
  private selectedCategory?: SoapCategory;

  constructor(private soapNotesService: SoapNotesService) {}

  ngOnInit(): void {
    const selectedPayload = this.isPayloadConfig(this.payload) ? this.payload : undefined;
    const selected = Array.isArray(selectedPayload?.selected) ? selectedPayload.selected : [];
    this.selected = new Set(selected.map((item) => String(item)));
    this.selectedCategory = this.normalizeCategory(selectedPayload?.soapCategory);
    this.loadData();
  }

  toggleAttribute(attribute: SoapAttribute): void {
    const key = String(attribute.id);
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      this.selected.add(key);
    }

    this.payload = this.getSelectedAttributes();
  }

  isSelected(attribute: SoapAttribute): boolean {
    return this.selected.has(String(attribute.id));
  }

  applyFilter(): void {
    const baseAttributes = this.categoryAttributes;
    if (this.selectedGroupId === 'all') {
      this.filteredAttributes = [...baseAttributes];
      return;
    }

    const fallbackGroupId = (attr: SoapAttribute): string | undefined => {
      const category = attr.soapCategory?.toUpperCase();
      return category ? this.categoryGroupMap.get(category) : undefined;
    };

    this.filteredAttributes = baseAttributes.filter((attr) => {
      const groupIds = attr.groupIds ?? [];
      if (groupIds.length > 0) {
        return groupIds.includes(this.selectedGroupId);
      }
      return fallbackGroupId(attr) === this.selectedGroupId;
    });
  }

  private loadData(): void {
    this.soapNotesService
      .getSoapGroupsCatalog()
      .pipe(take(1))
      .subscribe((groups) => {
        this.groupOptions = [{ id: 'all', name: 'All Groups' }, ...groups];
        this.selectedGroupId = 'all';
        this.categoryGroupMap = this.buildCategoryGroupMap(groups);
        this.isLoadingGroups = false;
        this.isLoading = this.isLoadingGroups || this.isLoadingAttributes;
        if (!this.isLoadingAttributes) {
          this.applyFilter();
        }
      });

    this.soapNotesService
      .getSoapAttributesCatalog()
      .pipe(take(1))
      .subscribe((attributes) => {
        this.attributes = attributes;
        this.categoryAttributes = this.filterByCategory(attributes);
        this.applyFilter();
        this.payload = this.getSelectedAttributes();
        this.isLoadingAttributes = false;
        this.isLoading = this.isLoadingGroups || this.isLoadingAttributes;
        if (!this.isLoadingGroups) {
          this.applyFilter();
        }
      });
  }

  private buildCategoryGroupMap(groups: SoapGroup[]): Map<string, string> {
    const map = new Map<string, string>();
    groups.forEach((group) => {
      const name = group.name.toLowerCase();
      if (name.includes('subjective')) {
        map.set('S', String(group.id));
      }
      if (name.includes('objective')) {
        map.set('O', String(group.id));
      }
      if (name.includes('assessment')) {
        map.set('A', String(group.id));
      }
      if (name.includes('plan')) {
        map.set('P', String(group.id));
      }
    });
    return map;
  }

  private getSelectedAttributes(): SoapAttributeSelection[] {
    return this.categoryAttributes
      .filter((attr) => this.selected.has(String(attr.id)))
      .map((attr) => ({ id: attr.id, name: attr.name }));
  }

  private filterByCategory(attributes: SoapAttribute[]): SoapAttribute[] {
    if (!this.selectedCategory) {
      return attributes;
    }

    return attributes.filter(
      (attribute) => attribute.soapCategory?.toUpperCase() === this.selectedCategory,
    );
  }

  private normalizeCategory(value?: SoapCategory): SoapCategory | undefined {
    if (!value) {
      return undefined;
    }

    const normalized = value.toUpperCase() as SoapCategory;
    return normalized === 'S' || normalized === 'O' || normalized === 'A' || normalized === 'P'
      ? normalized
      : undefined;
  }

  private isPayloadConfig(
    payload: SoapAttributesDialogValue,
  ): payload is SoapAttributesDialogPayload {
    return !Array.isArray(payload);
  }
}
